import React, { useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { DEDUCT_USER_DIAMONDS } from './graphql';
import { downloadCsv, ParseResult, parseEntries, toCsv } from './parse';

const MAX_ENTRIES = 1000;
/**
 * How many players go in one request to the server. Not exposed in the UI - the operator has no
 * reason to tune it. The server handles each player in its own transaction, so this only trades
 * request count against how often the progress list refreshes. At 25 a request stays a couple of
 * seconds (well inside any gateway timeout) and a full 1000-player list still refreshes 40 times.
 */
const PLAYERS_PER_REQUEST = 25;
/** Sent with every run so the server audit log records where the change came from. */
const DEDUCTION_REASON = 'Diamond exploit clawback';

/** Client-side row state. PENDING/PROCESSING are UI-only; the rest come from the server. */
type RowStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'DEDUCTED'
  | 'CLAMPED_TO_MIN'
  | 'SKIPPED_BELOW_MIN'
  | 'USER_NOT_FOUND'
  | 'INVALID_AMOUNT'
  | 'FAILED';

type DeductionRow = {
  userId: string;
  requestedAmount: number;
  status: RowStatus;
  balanceBefore?: number | null;
  balanceAfter?: number | null;
  actualDeducted?: number | null;
  error?: string | null;
};

const RESULT_COLUMNS = [
  'userId',
  'status',
  'requestedAmount',
  'balanceBefore',
  'balanceAfter',
  'actualDeducted',
  'error',
];

const statusVariant = (status: RowStatus): string => {
  switch (status) {
    case 'DEDUCTED':
      return 'success';
    case 'CLAMPED_TO_MIN':
      return 'info';
    case 'SKIPPED_BELOW_MIN':
      return 'secondary';
    case 'PROCESSING':
      return 'primary';
    case 'PENDING':
      return 'light';
    default:
      return 'danger';
  }
};

const DiamondDeduction = () => {
  const client = useApolloClient();

  const [rawInput, setRawInput] = useState('');
  const [filename, setFilename] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const [rows, setRows] = useState<DeductionRow[]>([]);
  const [running, setRunning] = useState(false);
  const [runWasDryRun, setRunWasDryRun] = useState(true);
  const [completedDryRunFor, setCompletedDryRunFor] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const abortRef = useRef(false);

  // Only set when the operator presses "Check list" (or loads a file) - never on every keystroke.
  const [parsed, setParsed] = useState<ParseResult | null>(null);

  const entries = parsed?.entries ?? [];
  const totalRequested = entries.reduce((sum, entry) => sum + entry.amount, 0);
  const tooManyEntries = entries.length > MAX_ENTRIES;
  const canRun = entries.length > 0 && !tooManyEntries && !running;

  // Identifies the exact list, so we can tell whether the preview applies to what is loaded now.
  const listSignature = entries.map((entry) => `${entry.userId}:${entry.amount}`).join('|');
  const dryRunMatchesCurrentList = completedDryRunFor === listSignature && listSignature.length > 0;

  const done = rows.filter((row) => row.status !== 'PENDING' && row.status !== 'PROCESSING');
  const deducted = done.filter((row) => (row.actualDeducted ?? 0) > 0);
  const skipped = done.filter((row) => row.status === 'SKIPPED_BELOW_MIN');
  const failed = done.filter((row) => ['FAILED', 'USER_NOT_FOUND', 'INVALID_AMOUNT'].includes(row.status));
  const totalDeducted = deducted.reduce((sum, row) => sum + (row.actualDeducted ?? 0), 0);
  const progressPct = rows.length ? Math.round((done.length / rows.length) * 100) : 0;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setRawInput(text);
      // Loading a file is one discrete action, so check it straight away.
      setParsed(parseEntries(text));
    };
    reader.readAsText(file);
  };

  const patchRows = (userIds: string[], patch: Partial<DeductionRow>) => {
    const ids = new Set(userIds);
    setRows((prev) => prev.map((row) => (ids.has(row.userId) ? { ...row, ...patch } : row)));
  };

  /**
   * Runs the batch in sequential chunks so the operator sees progress as it happens. The backend
   * processes each user in its own transaction, so splitting the call changes nothing about
   * correctness - a chunk that fails leaves earlier chunks already applied.
   */
  const run = async (dryRun: boolean) => {
    abortRef.current = false;
    setFatalError(null);
    setRunning(true);
    setRunWasDryRun(dryRun);

    setRows(
      entries.map((entry) => ({
        userId: entry.userId,
        requestedAmount: entry.amount,
        status: 'PENDING' as RowStatus,
      }))
    );

    for (let i = 0; i < entries.length; i += PLAYERS_PER_REQUEST) {
      if (abortRef.current) break;

      const chunk = entries.slice(i, i + PLAYERS_PER_REQUEST);
      const chunkIds = chunk.map((entry) => entry.userId);
      patchRows(chunkIds, { status: 'PROCESSING' });

      try {
        const { data } = await client.mutate({
          mutation: DEDUCT_USER_DIAMONDS,
          variables: { input: { entries: chunk, reason: DEDUCTION_REASON, dryRun } },
          fetchPolicy: 'no-cache',
        });

        const results = data?.deductUserDiamonds?.results ?? [];
        const byUserId = new Map<string, DeductionRow>();
        results.forEach((result: DeductionRow) => byUserId.set(result.userId, result));

        setRows((prev) =>
          prev.map((row) => {
            const result = byUserId.get(row.userId);
            if (!result || !chunkIds.includes(row.userId)) return row;
            return {
              ...row,
              status: result.status,
              balanceBefore: result.balanceBefore,
              balanceAfter: result.balanceAfter,
              actualDeducted: result.actualDeducted,
              error: result.error,
            };
          })
        );
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        patchRows(chunkIds, { status: 'FAILED', error: message });
        setFatalError(`Chunk starting at row ${i + 1} failed: ${message}`);
      }
    }

    if (dryRun && !abortRef.current) {
      setCompletedDryRunFor(listSignature);
    }
    setRunning(false);
  };

  const handleApply = () => {
    setShowConfirm(false);
    run(false);
  };

  const handleDownload = () => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const csvRows = rows.map((row) => ({
      ...row,
      reason: DEDUCTION_REASON,
      previewOnly: runWasDryRun,
      exportedAt: new Date().toISOString(),
    }));
    downloadCsv(
      `diamond-deduction-${runWasDryRun ? 'preview' : 'applied'}-${stamp}.csv`,
      toCsv(csvRows, [...RESULT_COLUMNS, 'reason', 'previewOnly', 'exportedAt'])
    );
  };

  return (
    <div className="p-3">
      <h3>Diamond Deduction</h3>
      <Alert variant="warning">
        Always <strong>preview</strong> first to see what would happen, then download the CSV after applying
      </Alert>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title>1. Users and amounts</Card.Title>
          <Form.Group>
            <Form.Label>Upload CSV</Form.Label>
            <Form.Control type="file" accept="text/csv,.csv,.txt" onChange={handleFile} />
            <Form.Text muted>
              One entry per line: <code>userId,amount</code>. A header row is ignored. Semicolon and tab separators also
              work.
              {filename && (
                <>
                  {' '}
                  Loaded: <strong>{filename}</strong>
                </>
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Or paste / type entries (a single user is just one line)</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder="5d2b989c-527e-4c72-97da-cf7f9bf44727,2500"
              value={rawInput}
              onChange={(e) => {
                setRawInput(e.target.value);
                // Discard the previous check so stale counts are never shown as current.
                setParsed(null);
              }}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Group>

          <Button
            variant="primary"
            className="mt-2"
            disabled={!rawInput.trim() || running}
            onClick={() => setParsed(parseEntries(rawInput))}
          >
            Check list
          </Button>

          {!parsed && rawInput.trim() && (
            <Alert variant="secondary" className="mt-2 mb-0">
              Press <strong>Check list</strong> to validate the entries before running.
            </Alert>
          )}

          {parsed && (
            <>
              <div className="mt-2">
                <Badge variant="success" className="mr-2">
                  {entries.length} valid
                </Badge>
                <Badge variant={(parsed?.invalidLines ?? []).length ? 'danger' : 'light'} className="mr-2">
                  {(parsed?.invalidLines ?? []).length} invalid
                </Badge>
                <Badge variant={(parsed?.duplicateUserIds ?? []).length ? 'warning' : 'light'} className="mr-2">
                  {(parsed?.duplicateUserIds ?? []).length} duplicate userIds
                </Badge>
                <Badge variant="light">total requested: {totalRequested.toLocaleString()} diamonds</Badge>
              </div>

              {tooManyEntries && (
                <Alert variant="danger" className="mt-2">
                  {entries.length} entries exceeds the {MAX_ENTRIES} per-run limit. Split the file.
                </Alert>
              )}

              {(parsed?.duplicateUserIds ?? []).length > 0 && (
                <Alert variant="warning" className="mt-2">
                  Duplicate userIds — only the <strong>last</strong> amount for each is used:{' '}
                  <code>{(parsed?.duplicateUserIds ?? []).join(', ')}</code>
                </Alert>
              )}

              {(parsed?.invalidLines ?? []).length > 0 && (
                <Alert variant="danger" className="mt-2">
                  <strong>Ignored lines</strong>
                  <Table size="sm" className="mt-2 mb-0">
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Content</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(parsed?.invalidLines ?? []).slice(0, 20).map((bad) => (
                        <tr key={bad.line}>
                          <td>{bad.line}</td>
                          <td>
                            <code>{bad.raw}</code>
                          </td>
                          <td>{bad.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {(parsed?.invalidLines ?? []).length > 20 && (
                    <em>…and {(parsed?.invalidLines ?? []).length - 20} more</em>
                  )}
                </Alert>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title>2. Run</Card.Title>
          <div>
            <Button variant="secondary" className="mr-2" disabled={!canRun} onClick={() => run(true)}>
              Preview (no change)
            </Button>
            <Button variant="danger" className="mr-2" disabled={!canRun} onClick={() => setShowConfirm(true)}>
              Apply deduction
            </Button>
            {running && (
              <Button
                variant="outline-dark"
                className="mr-2"
                onClick={() => {
                  abortRef.current = true;
                }}
              >
                Stop after current batch
              </Button>
            )}
            {rows.length > 0 && !running && (
              <Button variant="outline-primary" onClick={handleDownload}>
                Download CSV
              </Button>
            )}
            {running && <Spinner animation="border" size="sm" className="ml-2" />}
          </div>
        </Card.Body>
      </Card>

      {fatalError && <Alert variant="danger">{fatalError}</Alert>}

      {rows.length > 0 && (
        <Card>
          <Card.Body>
            <Card.Title>
              Progress{' '}
              <Badge variant={runWasDryRun ? 'secondary' : 'danger'}>
                {runWasDryRun ? 'PREVIEW — no change' : 'CHANGES APPLIED'}
              </Badge>
            </Card.Title>

            <ProgressBar
              className="mb-3"
              now={progressPct}
              label={`${done.length}/${rows.length}`}
              variant={failed.length ? 'warning' : 'success'}
              animated={running}
            />

            <Row className="mb-3">
              <Col>
                Done: <strong>{done.length}</strong> / {rows.length}
              </Col>
              <Col>
                Deducted: <strong>{deducted.length}</strong>
              </Col>
              <Col>
                Skipped: <strong>{skipped.length}</strong>
              </Col>
              <Col>
                Failed: <strong>{failed.length}</strong>
              </Col>
              <Col>
                Total diamonds: <strong>{totalDeducted.toLocaleString()}</strong>
              </Col>
            </Row>

            {!runWasDryRun && !running && (
              <Alert variant="info">
                Download the CSV now — this table is the only copy on this page and it disappears if you reload. (A
                permanent copy is also kept in the server logs.)
              </Alert>
            )}

            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User ID</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Actually deducted</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.userId}>
                    <td>{index + 1}</td>
                    <td>
                      <code>{row.userId}</code>
                    </td>
                    <td>
                      <Badge variant={statusVariant(row.status)}>
                        {row.status === 'PROCESSING' ? (
                          <>
                            <Spinner animation="border" size="sm" className="mr-1" /> PROCESSING
                          </>
                        ) : (
                          row.status
                        )}
                      </Badge>
                    </td>
                    <td>{row.requestedAmount?.toLocaleString()}</td>
                    <td>{row.balanceBefore?.toLocaleString()}</td>
                    <td>{row.balanceAfter?.toLocaleString()}</td>
                    <td>{row.actualDeducted?.toLocaleString()}</td>
                    <td>{row.error}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Apply diamond deduction?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will deduct diamonds from <strong>{entries.length}</strong> user
            {entries.length === 1 ? '' : 's'} — up to <strong>{totalRequested.toLocaleString()}</strong> diamonds in
            total.
          </p>
          {dryRunMatchesCurrentList ? (
            <Alert variant="success" className="mt-3 mb-0">
              A preview has been completed for this exact list.
            </Alert>
          ) : (
            <Alert variant="warning" className="mt-3 mb-0">
              No preview has been run for this exact list. Consider cancelling and previewing first.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleApply}>
            Yes, deduct
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DiamondDeduction;
